import * as yup from 'yup';

export const VoucherInformationSchema = yup.object({
  title: yup
    .string()
    .required('Nama voucher wajib diisi')
    .max(100, 'Maksimal 100 karakter'),
  code: yup
    .string()
    .required('Kode voucher wajib diisi')
    .max(50, 'Maksimal 50 karakter'),
  codeValidated: yup
    .boolean()
    .oneOf(
      [true],
      "Kode voucher belum diverifikasi. Klik tombol 'Cek Kode' untuk memvalidasi ketersediaan kode."
    )
    .required(),
  desc: yup.string().max(200, 'Maksimal 200 karakter'),
  period_start_date: yup
    .string()
    .required('Tanggal mulai wajib diisi')
    .test('date-format', 'Format tanggal tidak valid', (value) => {
      if (!value) return false;
      return /^\d{4}-\d{2}-\d{2}$/.test(value);
    }),
  period_end_date: yup
    .string()
    .required('Tanggal berakhir wajib diisi')
    .test('date-format', 'Format tanggal tidak valid', (value) => {
      if (!value) return false;
      return /^\d{4}-\d{2}-\d{2}$/.test(value);
    })
    .test(
      'end-after-start',
      'Tanggal berakhir harus setelah tanggal mulai',
      (value, context) => {
        const { period_start_date } = context.parent;
        if (!value || !period_start_date) return true;
        return new Date(value) >= new Date(period_start_date);
      }
    ),
  banner_url: yup.string().required('Banner voucher wajib diunggah'),
});

export type VoucherInformationFormData = yup.InferType<
  typeof VoucherInformationSchema
>;

// Target Configuration Schema
export const VoucherTargetConfigurationSchema = yup.object({
  target_type: yup
    .string()
    .oneOf(['GENERAL', 'SHIP_TO'], 'Tipe target tidak valid')
    .default('GENERAL'),
  business_segmentations: yup.array().of(yup.string()).default([]),
  distribution_channels: yup.array().of(yup.string()).default([]),
  business_categories: yup.array().of(yup.string()).default([]),
  shipto_csv_url: yup.string().nullable(),
  quota: yup
    .number()
    .transform((value, originalValue) => {
      // Handle NaN from valueAsNumber
      if (
        Number.isNaN(value) ||
        originalValue === '' ||
        originalValue === null
      ) {
        return undefined;
      }
      return value;
    })
    .typeError('Qty voucher harus berupa angka')
    .required('Qty voucher wajib diisi')
    .min(1, 'Minimal 1')
    .max(9999999, 'Maksimal 9.999.999')
    .default(1),
  max_use_per_user: yup
    .number()
    .transform((value, originalValue) => {
      // Handle NaN from valueAsNumber
      if (
        Number.isNaN(value) ||
        originalValue === '' ||
        originalValue === null
      ) {
        return undefined;
      }
      return value;
    })
    .typeError('Maks. pakai per shipto. harus berupa angka')
    .required('Maks. pakai per shipto. wajib diisi')
    .min(1, 'Minimal 1')
    .test(
      'max-quota',
      'Maks. pakai per shipto. tidak boleh lebih besar dari Qty. Voucher Beredar',
      (value, context) => {
        const { quota } = context.parent;
        if (!value || !quota) return true;
        return value <= quota;
      }
    ),
});

// Acquisition Configuration Schema
export const VoucherAcquisitionConfigurationSchema = yup.object({
  acquisition_type: yup
    .string()
    .oneOf(['REGULAR', 'ACCOUNT_ACTIVATION'], 'Cara mendapatkan tidak valid')
    .default('REGULAR'),
  valid_day_since_account_signup: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? undefined : value
    )
    .nullable()
    .when('acquisition_type', {
      is: 'ACCOUNT_ACTIVATION',
      then: (schema) =>
        schema
          .required('Berlaku sejak wajib diisi untuk aktivasi akun')
          .min(1, 'Minimal 1 hari'),
      otherwise: (schema) => schema.nullable(),
    }),
});

// Condition Configuration Schema
export const VoucherConditionConfigurationSchema = yup.object({
  condition_type: yup
    .string()
    .oneOf(['REGULAR', 'MIN_PURCHASE'], 'Jenis syarat tidak valid')
    .default('REGULAR'),
  is_allow_combine_other_voucher: yup.boolean().default(false),
  is_allow_combine_other_promo: yup.boolean().default(false),
  is_allow_combine_other_special_price: yup.boolean().default(false),
  min_purchase_amount: yup
    .number()
    .transform((value, originalValue) => {
      // Handle empty string, null, undefined, or NaN
      if (
        originalValue === '' ||
        originalValue === null ||
        originalValue === undefined ||
        Number.isNaN(value)
      ) {
        return undefined;
      }
      return value;
    })
    .nullable()
    .when('condition_type', {
      is: 'MIN_PURCHASE',
      then: (schema) =>
        schema
          .required(
            'Minimal nominal transaksi wajib diisi untuk syarat minimal belanja'
          )
          .min(1, 'Minimal nominal transaksi harus lebih dari 0'),
      otherwise: (schema) => schema.min(0, 'Minimal 0 jika diisi'),
    }),
});

// Reward Configuration Schema
export const VoucherRewardConfigurationSchema = yup.object({
  reward_type: yup
    .string()
    .oneOf(
      ['DISCOUNT_AMOUNT', 'DISCOUNT_PERCENT', 'FREE_GOODS'],
      'Jenis reward tidak valid'
    )
    .default('DISCOUNT_PERCENT'),
  is_bundling: yup.boolean().default(false),
  min_total_quantity_all_item: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? undefined : value
    )
    .nullable()
    .min(1, 'Minimal 1 jika diisi'),
  reward_rule_products: yup
    .array()
    .of(
      yup.object({
        product_item_code: yup
          .number()
          .typeError('Kode material produk tidak valid')
          .required(),
        min_quantity: yup
          .number()
          .transform((value, originalValue) =>
            originalValue === '' || originalValue === null ? undefined : value
          )
          .nullable()
          .min(1, 'Minimal 1 jika diisi'),
      })
    )
    .default([]),
  discount_products: yup
    .array()
    .of(
      yup.object({
        product_item_code: yup
          .number()
          .typeError('Kode material produk tidak valid')
          .required(),
        discount_percentage: yup
          .number()
          .transform((value, originalValue) =>
            originalValue === '' || originalValue === null ? undefined : value
          )
          .nullable()
          .min(0, 'Minimal 0%')
          .max(100, 'Maksimal 100%'),
        discount_amount: yup
          .number()
          .transform((value, originalValue) =>
            originalValue === '' || originalValue === null ? undefined : value
          )
          .nullable()
          .min(0, 'Minimal 0'),
      })
    )
    .default([]),
  free_goods: yup
    .array()
    .of(
      yup.object({
        product_item_code: yup
          .number()
          .typeError('Kode material produk tidak valid')
          .required(),
        quantity: yup
          .number()
          .transform((value, originalValue) =>
            originalValue === '' || originalValue === null ? undefined : value
          )
          .required('Kuantitas produk wajib diisi')
          .min(1, 'Minimal 1'),
      })
    )
    .default([]),
  discount_amount_value: yup.number().when('reward_type', {
    is: 'DISCOUNT_AMOUNT',
    then: (schema) =>
      schema
        .transform((value, originalValue) =>
          originalValue === '' || originalValue === null ? undefined : value
        )
        .typeError('Potongan harga wajib diisi')
        .required('Potongan harga wajib diisi')
        .min(0, 'Nilai minimal 0'),
    otherwise: (schema) => schema.nullable().default(null),
  }),
  discount_percentage_value: yup.number().when('reward_type', {
    is: 'DISCOUNT_PERCENT',
    then: (schema) =>
      schema
        .transform((value, originalValue) =>
          originalValue === '' || originalValue === null ? undefined : value
        )
        .typeError('Jumlah diskon wajib diisi')
        .required('Jumlah diskon wajib diisi')
        .min(0, 'Minimal 0%')
        .max(100, 'Maksimal 100%'),
    otherwise: (schema) => schema.nullable().default(null),
  }),
  free_goods_quantity_value: yup.number().when('reward_type', {
    is: 'FREE_GOODS',
    then: (schema) =>
      schema
        .transform((value, originalValue) =>
          originalValue === '' || originalValue === null ? undefined : value
        )
        .typeError('Kuantitas produk wajib diisi')
        .required('Kuantitas produk wajib diisi')
        .min(1, 'Minimal 1'),
    otherwise: (schema) => schema.nullable().default(null),
  }),
});

// Helper fields schema (for form UI)
export const VoucherHelperFieldsSchema = yup.object({
  codeValidated: yup
    .boolean()
    .oneOf([true], 'Silakan cek kode voucher terlebih dahulu')
    .required(),
  shipToCsv: yup
    .mixed<File[] | undefined>()
    .when('target_configuration.target_type', {
      is: 'SHIP_TO',
      then: (schema) =>
        schema.test(
          'ship-to-required',
          'File ship to wajib diupload',
          (value) => {
            if (!value) return false;
            return Array.isArray(value) && value.length > 0;
          }
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
  purchaseProducts: yup
    .array()
    .of(
      yup.object({
        value: yup.string(),
        label: yup.string(),
      })
    )
    .test('has-products', 'Minimal satu produk harus dipilih', (products) => {
      // Skip validation if no products exist (form not initialized)
      if (!products || products.length === 0) return true;

      // Check if all products are empty (initial state with placeholder)
      // This happens when useEnsurePurchaseProductField adds an empty product
      const allEmpty = products.every((p) => !p?.value || p.value === '');
      if (allEmpty) return true;

      // At least one product was interacted with, require at least one valid product
      const validProducts = products.filter((p) => p?.value && p.value !== '');
      return validProducts.length >= 1;
    }),
  rewardDiscountProduct: yup
    .mixed<{ value: string; label: string } | null>()
    .when('reward_configuration.reward_type', {
      is: (value: string) =>
        value === 'DISCOUNT_AMOUNT' || value === 'DISCOUNT_PERCENT',
      then: (schema) =>
        schema
          .nullable()
          .test(
            'discount-product-required',
            'Produk diskon wajib dipilih',
            (val) => !!val && !!val.value
          )
          .test(
            'discount-product-in-purchase-list',
            'Produk diskon harus ada di dalam daftar Ketentuan Pembelian Produk',
            function (val) {
              if (!val || !val.value) return true;

              const { purchaseProducts } = this.parent;
              if (!purchaseProducts || !Array.isArray(purchaseProducts))
                return true;

              const validPurchaseProducts = purchaseProducts.filter(
                (p: { value?: string }) => p?.value && p.value !== ''
              );

              return validPurchaseProducts.some(
                (p: { value: string }) => p.value === val.value
              );
            }
          ),
      otherwise: (schema) => schema.nullable(),
    }),
  rewardFreeProduct: yup
    .mixed<{ value: string; label: string } | null>()
    .when('reward_configuration.reward_type', {
      is: 'FREE_GOODS',
      then: (schema) =>
        schema
          .nullable()
          .test(
            'free-product-required',
            'Produk gratis wajib dipilih',
            (val) => !!val && !!val.value
          ),
      otherwise: (schema) => schema.nullable(),
    }),
});

// Complete Voucher Schema
export const VoucherTargetSchema = yup.object({
  // Configuration objects
  target_configuration: VoucherTargetConfigurationSchema.required(),
  acquisition_configuration: VoucherAcquisitionConfigurationSchema.required(),
  condition_configuration: VoucherConditionConfigurationSchema.required(),
  reward_configuration: VoucherRewardConfigurationSchema.required(),

  // Helper fields
  ...VoucherHelperFieldsSchema.fields,
});

export const VoucherFormSchema =
  VoucherInformationSchema.concat(VoucherTargetSchema);

export type VoucherTargetFormData = yup.InferType<typeof VoucherTargetSchema>;
export type VoucherFormData = yup.InferType<typeof VoucherFormSchema>;
